import { For, Show, createMemo, type Component } from 'solid-js';
import { Text } from '~ui/text';
import { formatPercent } from '~/lib/format';
import { boundsOf, monthsCovering, periodReturn, yearsCovering } from '~/lib/returns';
import { drawn } from '~/stores/compare-store';
import { Swatch } from '~/features/swatch';
import type { Basis } from '~/stores/series-store';

// Every selected fund, period by period.
//
// The overlay chart answers "which of these did better overall"; this answers
// "which of these did better *when*", which is a different question and the one
// that separates a fund that compounded from one that had a single good year.
// Two funds can end at the same place having got there in ways nobody would
// choose between blindly.
//
// Rows are periods and columns are funds, which is the transpose of the metrics
// table for the same reason it is the right way round there: you read one period
// across, and that only works if the period is the row.
//
// Diverging red/green, because zero is the meaningful middle and the two
// directions are opposites rather than more and less of one thing. Shaded
// relative to the biggest move on screen, so a quiet pair does not read as
// blank and a volatile one does not saturate throughout. Every cell carries its
// number — the fill is for finding a bad period while scanning, not for reading.

export const ComparePerformance: Component<{ grain: 'year' | 'month'; basis: Basis }> = (props) => {
    const funds = createMemo(() => drawn());

    const periods = createMemo(() => {
        const symbols = funds().map((f) => f.symbol);
        return props.grain === 'year' ? yearsCovering(symbols).map(String) : monthsCovering(symbols);
    });

    const grid = createMemo(() =>
        periods().map((label) => {
            const { from, to } = boundsOf(label);
            return { label, cells: funds().map((f) => periodReturn(f.symbol, props.basis, from, to)) };
        }),
    );

    const scale = createMemo(() => {
        const all = grid().flatMap((r) => r.cells.filter((v): v is number => v != null).map(Math.abs));
        return all.length === 0 ? 1 : Math.max(...all, 1);
    });

    const fill = (value: number | null): string => {
        if (value == null || value === 0) return 'transparent';
        const weight = Math.min(Math.abs(value) / scale(), 1) * 0.75;
        return `color-mix(in srgb, var(--color-${value > 0 ? 'green' : 'red'}) ${(weight * 100).toFixed(1)}%, transparent)`;
    };

    return (
        <Show
            when={funds().length > 0 && periods().length > 0}
            fallback={
                <div class='flex min-h-0 flex-1 items-center justify-center'>
                    <Text tone='muted'>{funds().length === 0 ? 'Select a fund' : 'No price history'}</Text>
                </div>
            }>
            <div class='min-h-0 flex-1 overflow-auto scrollbar-hide'>
                <table class='w-full table-fixed border-collapse'>
                    <thead class='sticky top-0 z-10 bg-background'>
                        <tr>
                            <th class='h-row w-20 border-b bg-background px-2 text-left text-sm font-normal'>
                                <Text tone='muted'>{props.grain === 'year' ? 'Year' : 'Month'}</Text>
                            </th>
                            <For each={funds()}>
                                {(f) => (
                                    <th class='h-row border-b border-l bg-background px-1 text-center text-sm font-normal'>
                                        {/* The same mark the fund carries on the
                                            chart above, so a column and a line
                                            are the same fund without being told. */}
                                        <span class='inline-flex items-center gap-1'>
                                            <Swatch color={f.color} dash={f.dash} />
                                            {f.symbol}
                                        </span>
                                    </th>
                                )}
                            </For>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={grid()}>
                            {(row) => (
                                <tr>
                                    <td class='h-row border-b px-2 text-sm'>
                                        <Text weight='medium'>{row.label}</Text>
                                    </td>
                                    <For each={row.cells}>
                                        {(value) => (
                                            <td class='h-row border-b border-l p-0 text-center' style={{ 'background-color': fill(value) }}>
                                                {/* A dash, not a zero: a fund that
                                                    had not listed yet did not
                                                    return nothing, it returned
                                                    nothing knowable. */}
                                                <span class='text-sm'>{value == null ? <Text tone='muted'>—</Text> : formatPercent(value, 1)}</span>
                                            </td>
                                        )}
                                    </For>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </div>
        </Show>
    );
};

export default ComparePerformance;
